import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin, Moon } from "lucide-react";
import PromoStrip from "@/components/site/PromoStrip";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";

export default function Packages() {
  const { format: fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => { (async () => { const { data } = await api.get("/packages"); setItems(data); })(); }, []);

  const book = async (p) => {
    if (!user) { toast.error("Please sign in to continue"); return; }
    const { data } = await api.post("/bookings", {
      booking_type: "package", item: p, travelers: [{ name: user.name }],
      total_amount: p.price, currency: "USD",
      contact: { email: user.email, name: user.name },
    });
    navigate(`/checkout/${data.id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 text-white">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Curated holiday packages</h1>
        <p className="mt-2 text-teal-50/90 max-w-xl">All-inclusive bundles handcrafted by our destination experts.</p>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
        {items.map((p) => (
          <Card key={p.id} data-testid={`package-card-${p.id}`} className="overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all">
            <div className="h-48 relative">
              <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
              <span className="absolute top-3 left-3 bg-[var(--brand-secondary)] text-[#064e3b] text-[11px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">Bundle</span>
            </div>
            <div className="p-5">
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">{p.title}</h3>
              <div className="flex items-center text-sm text-slate-500 mt-1">
                <MapPin className="w-3.5 h-3.5 mr-1" /> {p.destination}
                <span className="mx-2">·</span>
                <Moon className="w-3.5 h-3.5 mr-1" /> {p.nights} nights
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.inclusions.map((i) => <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-teal-50 text-teal-700">{i}</span>)}
              </div>
              <div className="flex items-end justify-between mt-4">
                <div>
                  <div className="text-xs text-slate-500">From</div>
                  <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">{fmt(p.price, { decimals: 0 })}</div>
                </div>
                <Button data-testid={`package-book-${p.id}`} onClick={() => book(p)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">Book package</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <PromoStrip />
    </div>
  );
}
